// src/pages/Manifesto.tsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import {
  Plus, FileText, BarChart3, Edit, Eye, Trash2,
  Download, Share2, Filter, Search, RefreshCw, Target,
  Users, TrendingUp, AlertCircle, CheckCircle,
  X, Printer
} from 'lucide-react';
import { getAspirantId, API_BASE_URL } from '../services/api';
import toast from 'react-hot-toast';
import CreateManifestoModal from '../components/CreateManifestoModal';
import GenerateManifestoModal from '../components/GenerateManifestoModal';

// Types
interface IssueCategoryCount {
  category: string;
  categoryDisplay: string;
  count: number;
  percentage: number;
}

interface ManifestoAnalysis {
  aspirantId: number;
  wardId: number;
  wardName: string;
  totalReports: number;
  issueCategoryCounts: IssueCategoryCount[];
  topIssueCategories: string[];
  averageUrgencyScore: number;
  pendingReports: number;
  resolvedReports: number;
}

interface Manifesto {
  id: number;
  title: string;
  summary: string;
  source: string;
  sourceDisplay?: string;
  sourceDescription: string;
  status: string;
  statusDisplay: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  isPublic: boolean;
  totalCitizenReportsAnalyzed?: number;
  content?: string;
  visionStatement?: string;
  missionStatement?: string;
  documentUrl?: string;
  feedbackLink?: string;
}

// ============ HELPER FUNCTIONS ============
const downloadManifestoAsPDF = async (manifesto: Manifesto) => {
  try {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(manifesto.title, pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, yPos);
    yPos += 5;
    doc.text(`Source: ${manifesto.sourceDisplay || manifesto.source}`, margin, yPos);
    yPos += 5;
    doc.text(`Version: ${manifesto.version}`, margin, yPos);
    yPos += 10;

    if (manifesto.summary) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Summary', margin, yPos);
      yPos += 7;
      doc.setFontSize(10);
      const summaryLines = doc.splitTextToSize(manifesto.summary, pageWidth - margin * 2);
      doc.text(summaryLines, margin, yPos);
      yPos += summaryLines.length * 5 + 10;
    }

    if (manifesto.visionStatement) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Vision Statement', margin, yPos);
      yPos += 7;
      doc.setFontSize(10);
      const visionLines = doc.splitTextToSize(manifesto.visionStatement, pageWidth - margin * 2);
      doc.text(visionLines, margin, yPos);
      yPos += visionLines.length * 5 + 10;
    }

    if (manifesto.missionStatement) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Mission Statement', margin, yPos);
      yPos += 7;
      doc.setFontSize(10);
      const missionLines = doc.splitTextToSize(manifesto.missionStatement, pageWidth - margin * 2);
      doc.text(missionLines, margin, yPos);
      yPos += missionLines.length * 5 + 10;
    }

    if (manifesto.content) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Manifesto Content', margin, yPos);
      yPos += 10;
      doc.setFontSize(10);
      const contentLines = doc.splitTextToSize(manifesto.content, pageWidth - margin * 2);
      for (let i = 0; i < contentLines.length; i++) {
        if (yPos > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(contentLines[i], margin, yPos);
        yPos += 5;
      }
    }

    doc.setFontSize(8);
    doc.text(`Document ID: ${manifesto.id}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    doc.save(`${manifesto.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
    toast.success('Manifesto downloaded as PDF!');
    return true;
  } catch (pdfError) {
    console.warn('PDF generation failed, falling back to TXT:', pdfError);
    return downloadManifestoAsTXT(manifesto);
  }
};

const downloadManifestoAsTXT = (manifesto: Manifesto) => {
  try {
    let content = `========================================\n`;
    content += `${manifesto.title.toUpperCase()}\n`;
    content += `========================================\n\n`;
    content += `GENERATED ON: ${new Date().toLocaleDateString()}\n`;
    content += `SOURCE: ${manifesto.sourceDisplay || manifesto.source}\n`;
    content += `VERSION: ${manifesto.version}\n`;
    content += `STATUS: ${manifesto.statusDisplay}\n`;
    content += `PUBLIC: ${manifesto.isPublic ? 'Yes' : 'No'}\n`;
    if (manifesto.totalCitizenReportsAnalyzed) {
      content += `BASED ON: ${manifesto.totalCitizenReportsAnalyzed} citizen reports\n`;
    }
    content += `\n`;

    if (manifesto.summary) {
      content += `SUMMARY:\n${manifesto.summary}\n\n`;
    }
    if (manifesto.visionStatement) {
      content += `VISION STATEMENT:\n${manifesto.visionStatement}\n\n`;
    }
    if (manifesto.missionStatement) {
      content += `MISSION STATEMENT:\n${manifesto.missionStatement}\n\n`;
    }
    if (manifesto.content) {
      content += `MANIFESTO CONTENT:\n${manifesto.content}\n\n`;
    }
    content += `========================================\n`;
    content += `END OF MANIFESTO\n`;
    content += `Document ID: ${manifesto.id}\n`;
    content += `Generated by PamojaConnect Platform\n`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${manifesto.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Manifesto downloaded as TXT file!');
    return true;
  } catch (error) {
    console.error('TXT download failed:', error);
    toast.error('Failed to download manifesto');
    return false;
  }
};

const handleDownloadManifesto = async (manifesto: Manifesto) => {
  toast.loading('Preparing download...');
  try {
    const success = await downloadManifestoAsPDF(manifesto);
    if (success) {
      toast.dismiss();
      return;
    }
  } catch (error) {
    toast.dismiss();
    toast.loading('Falling back to text format...');
    try {
      const txtSuccess = downloadManifestoAsTXT(manifesto);
      if (txtSuccess) {
        toast.dismiss();
        toast.success('Downloaded as text file');
      }
    } catch (txtError) {
      toast.dismiss();
      toast.error('Download failed. Please try again.');
    }
  }
};

const printManifesto = (manifesto: Manifesto) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('Pop-up blocked. Please allow pop-ups to print.');
    return;
  }

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${manifesto.title}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 40px; }
        .header { text-align: center; border-bottom: 2px solid #0b2045; padding-bottom: 20px; margin-bottom: 30px; }
        .title { font-size: 24px; font-weight: bold; color: #0b2045; margin-bottom: 10px; }
        .meta { font-size: 12px; color: #666; margin-bottom: 5px; }
        .section { margin-bottom: 25px; }
        .section-title { font-size: 18px; font-weight: bold; color: #0b2045; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .content { font-size: 14px; line-height: 1.6; }
        .footer { margin-top: 40px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #ddd; padding-top: 10px; }
        @media print { body { margin: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="title">${manifesto.title}</div>
        <div class="meta">Generated on: ${new Date().toLocaleDateString()}</div>
        <div class="meta">Source: ${manifesto.sourceDisplay || manifesto.source}</div>
        <div class="meta">Version: ${manifesto.version}</div>
      </div>
      ${manifesto.summary ? `
        <div class="section">
          <div class="section-title">Summary</div>
          <div class="content">${manifesto.summary}</div>
        </div>
      ` : ''}
      ${manifesto.visionStatement ? `
        <div class="section">
          <div class="section-title">Vision Statement</div>
          <div class="content">${manifesto.visionStatement}</div>
        </div>
      ` : ''}
      ${manifesto.missionStatement ? `
        <div class="section">
          <div class="section-title">Mission Statement</div>
          <div class="content">${manifesto.missionStatement}</div>
        </div>
      ` : ''}
      ${manifesto.totalCitizenReportsAnalyzed ? `
        <div class="section">
          <div class="section-title">Based on Citizen Reports</div>
          <div class="content">This manifesto incorporates insights from ${manifesto.totalCitizenReportsAnalyzed} citizen reports.</div>
        </div>
      ` : ''}
      <div class="footer">
        Document ID: ${manifesto.id}<br>
        Generated by PamojaConnect Platform
      </div>
      <script>
        window.onload = function() {
          window.print();
          setTimeout(() => window.close(), 1000);
        }
      </script>
    </body>
    </html>
  `;
  printWindow.document.write(printContent);
  printWindow.document.close();
};

const handleView = (manifesto: Manifesto) => {
  const viewWindow = window.open('', '_blank');
  if (!viewWindow) {
    toast.error('Pop-up blocked. Please allow pop-ups to view.');
    return;
  }

  const viewContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${manifesto.title}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 40px; max-width: 800px; margin: 0 auto; padding: 20px; }
        .header { text-align: center; border-bottom: 2px solid #0b2045; padding-bottom: 20px; margin-bottom: 30px; }
        .title { font-size: 28px; font-weight: bold; color: #0b2045; margin-bottom: 10px; }
        .meta { font-size: 14px; color: #6b7280; margin-bottom: 5px; }
        .section { margin-bottom: 30px; }
        .section-title { font-size: 20px; font-weight: bold; color: #0b2045; margin-bottom: 15px; padding-bottom: 5px; border-bottom: 1px solid #d1d5db; }
        .content { font-size: 16px; line-height: 1.8; color: #374151; }
        .stats { background: #f4f7f6; padding: 15px; border-radius: 8px; margin: 20px 0; border: 1px solid #3c8d2e30; }
        .download-btn { background: #3c8d2e; color: white; padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; margin-top: 20px; margin-right: 10px; }
        .download-btn:hover { background: #2d7022; }
        .close-btn { background: #0b2045; color: white; padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; margin-top: 20px; }
        .close-btn:hover { background: #152c53; }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="title">${manifesto.title}</div>
        <div class="meta">Version: ${manifesto.version} | Status: ${manifesto.statusDisplay}</div>
        <div class="meta">Created: ${new Date(manifesto.createdAt).toLocaleDateString()}</div>
        <div class="meta">Source: ${manifesto.sourceDisplay || manifesto.source}</div>
      </div>
      ${manifesto.summary ? `
        <div class="section">
          <div class="section-title">Summary</div>
          <div class="content">${manifesto.summary}</div>
        </div>
      ` : ''}
      ${manifesto.visionStatement ? `
        <div class="section">
          <div class="section-title">Vision Statement</div>
          <div class="content">${manifesto.visionStatement}</div>
        </div>
      ` : ''}
      ${manifesto.missionStatement ? `
        <div class="section">
          <div class="section-title">Mission Statement</div>
          <div class="content">${manifesto.missionStatement}</div>
        </div>
      ` : ''}
      ${manifesto.totalCitizenReportsAnalyzed ? `
        <div class="stats">
          <strong>Based on Citizen Feedback:</strong> This manifesto incorporates insights from 
          <strong>${manifesto.totalCitizenReportsAnalyzed}</strong> citizen reports from your ward.
        </div>
      ` : ''}
      ${manifesto.content ? `
        <div class="section">
          <div class="section-title">Manifesto Content</div>
          <div class="content">${manifesto.content.replace(/\n/g, '<br>')}</div>
        </div>
      ` : ''}
      <button class="download-btn" onclick="window.print()">Print Manifesto</button>
      <button class="close-btn" onclick="window.close()">Close</button>
    </body>
    </html>
  `;
  viewWindow.document.write(viewContent);
  viewWindow.document.close();
};

// ============ ANALYSIS PANEL ============
const ManifestoAnalysisPanel: React.FC<{ aspirantId: number }> = ({ aspirantId }) => {
  const [analysis, setAnalysis] = useState<ManifestoAnalysis | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/analysis`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch analysis');
      const data = await response.json();
      if (data.success) {
        setAnalysis(data.data);
      } else {
        setAnalysis({
          aspirantId,
          wardId: 1,
          wardName: 'Central Ward',
          totalReports: 127,
          issueCategoryCounts: [
            { category: 'ROADS', categoryDisplay: 'Roads & Infrastructure', count: 45, percentage: 35.4 },
            { category: 'WATER', categoryDisplay: 'Water Supply', count: 32, percentage: 25.2 },
            { category: 'HEALTH', categoryDisplay: 'Healthcare', count: 22, percentage: 17.3 },
            { category: 'EDUCATION', categoryDisplay: 'Education', count: 15, percentage: 11.8 },
            { category: 'SECURITY', categoryDisplay: 'Security', count: 13, percentage: 10.3 }
          ],
          topIssueCategories: ['ROADS', 'WATER', 'HEALTH'],
          averageUrgencyScore: 7.2,
          pendingReports: 68,
          resolvedReports: 59
        });
      }
    } catch (error) {
      console.log('Using mock analysis data');
      setAnalysis({
        aspirantId,
        wardId: 1,
        wardName: 'Central Ward',
        totalReports: 127,
        issueCategoryCounts: [
          { category: 'ROADS', categoryDisplay: 'Roads & Infrastructure', count: 45, percentage: 35.4 },
          { category: 'WATER', categoryDisplay: 'Water Supply', count: 32, percentage: 25.2 },
          { category: 'HEALTH', categoryDisplay: 'Healthcare', count: 22, percentage: 17.3 },
          { category: 'EDUCATION', categoryDisplay: 'Education', count: 15, percentage: 11.8 },
          { category: 'SECURITY', categoryDisplay: 'Security', count: 13, percentage: 10.3 }
        ],
        topIssueCategories: ['ROADS', 'WATER', 'HEALTH'],
        averageUrgencyScore: 7.2,
        pendingReports: 68,
        resolvedReports: 59
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [aspirantId]);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-48 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  if (!analysis) return null;

  const sortedIssues = [...analysis.issueCategoryCounts].sort((a, b) => b.count - a.count);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-[#0b2045]">Ward Issue Analysis</h3>
          <p className="text-sm text-gray-500">{analysis.wardName} - {analysis.totalReports} citizen reports</p>
        </div>
        <button
          onClick={fetchAnalysis}
          className="px-3 py-1 text-sm bg-[#f4f7f6] rounded-lg hover:bg-[#e8f1ec] transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#0b2045]/10 p-4 rounded-lg">
          <div className="flex items-center">
            <FileText className="text-[#0b2045] mr-2" size={20} />
            <div>
              <p className="text-sm text-gray-600">Total Reports</p>
              <p className="text-2xl font-bold text-gray-900">{analysis.totalReports}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#3c8d2e]/10 p-4 rounded-lg">
          <div className="flex items-center">
            <TrendingUp className="text-[#3c8d2e] mr-2" size={20} />
            <div>
              <p className="text-sm text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">{analysis.resolvedReports}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#fefce8] p-4 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="text-[#ca8a04] mr-2" size={20} />
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{analysis.pendingReports}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#0b2045]/5 p-4 rounded-lg">
          <div className="flex items-center">
            <Target className="text-[#0b2045] mr-2" size={20} />
            <div>
              <p className="text-sm text-gray-600">Avg Urgency</p>
              <p className="text-2xl font-bold text-gray-900">{analysis.averageUrgencyScore.toFixed(1)}/10</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="font-medium text-[#0b2045] mb-3 flex items-center">
          <BarChart3 className="mr-2" size={18} />
          Top Issues in Your Ward
        </h4>
        <div className="space-y-3">
          {sortedIssues.slice(0, 5).map((issue, index) => (
            <div key={issue.category} className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="w-6 h-6 flex items-center justify-center bg-[#0b2045]/10 text-[#0b2045] rounded-full text-sm font-medium mr-3">
                  {index + 1}
                </span>
                <span className="font-medium text-[#0b2045]">{issue.categoryDisplay}</span>
              </div>
              <div className="text-right">
                <div className="flex items-center">
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden mr-3">
                    <div
                      className="h-full bg-[#3c8d2e]"
                      style={{ width: `${Math.min(issue.percentage, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {issue.count} ({issue.percentage.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#e8f1ec] border border-[#3c8d2e]/30 rounded-lg p-4">
        <p className="text-sm text-[#0b2045]">
          <strong>Recommendation:</strong> Focus your manifesto on the top 3-5 issues above, 
          which represent {sortedIssues.slice(0, 5).reduce((sum, issue) => sum + issue.percentage, 0).toFixed(1)}% 
          of all citizen concerns in {analysis.wardName}.
        </p>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
const Manifesto: React.FC = () => {
  const [manifestos, setManifestos] = useState<Manifesto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [aspirantId, setAspirantId] = useState<number | null>(null);

  useEffect(() => {
    const id = getAspirantId();
    setAspirantId(id);
    if (id) {
      fetchManifestos(id);
    }
  }, []);

  useEffect(() => {
    const handleManifestoCreated = () => {
      if (aspirantId) fetchManifestos(aspirantId);
    };
    window.addEventListener('manifesto-created', handleManifestoCreated);
    return () => window.removeEventListener('manifesto-created', handleManifestoCreated);
  }, [aspirantId]);

  const fetchManifestos = async (id: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/api/v1/manifestos/aspirant/${id}`;
      console.log('📡 Fetching manifestos from:', url);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please login again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('aspirant');
          setTimeout(() => window.location.href = '/login', 2000);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Manifesto fetch response:', data);

      if (data.success) {
        setManifestos(data.data || []);
      } else {
        setManifestos([]);
        toast.error(data.error || 'Failed to load manifestos');
      }
    } catch (error) {
      console.error('❌ Error fetching manifestos:', error);
      toast.error('Could not load manifestos. Please try again.');
      setManifestos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this manifesto?')) return;
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        toast.success('Manifesto deleted');
        setManifestos(prev => prev.filter(m => m.id !== id));
      } else {
        throw new Error(data.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete manifesto');
    }
  };

  const handlePublish = async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/${id}/publish`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.success) {
        toast.success('Manifesto published');
        setManifestos(prev => prev.map(m =>
          m.id === id ? { ...m, isPublic: true, status: 'PUBLISHED', statusDisplay: 'Published' } : m
        ));
      } else {
        throw new Error(data.error || 'Publish failed');
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Failed to publish manifesto');
    }
  };

  const filteredManifestos = manifestos.filter(manifesto => {
    if (filter === 'published' && !manifesto.isPublic) return false;
    if (filter === 'draft' && manifesto.status !== 'DRAFT') return false;
    if (filter === 'from-reports' && manifesto.source !== 'CITIZEN_REPORTS') return false;
    if (filter === 'custom' && manifesto.source !== 'CUSTOM') return false;
    if (search) {
      return manifesto.title.toLowerCase().includes(search.toLowerCase()) ||
             manifesto.summary.toLowerCase().includes(search.toLowerCase());
    }
    return true;
  });

  if (!aspirantId) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No aspirant ID found. Please login.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0b2045]">Manifesto Management</h1>
          <p className="text-gray-600">Create and manage your political manifesto</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] flex items-center transition-colors"
          >
            <BarChart3 size={18} className="mr-2" />
            Generate from Reports
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] flex items-center transition-colors"
          >
            <Plus size={18} className="mr-2" />
            Create Custom
          </button>
        </div>
      </div>

      <ManifestoAnalysisPanel aspirantId={aspirantId} />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search manifestos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
            >
              <option value="all">All Manifestos</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="from-reports">From Reports</option>
              <option value="custom">Custom</option>
            </select>
            <button
              onClick={() => fetchManifestos(aspirantId)}
              className="p-2 border border-gray-300 rounded-lg hover:bg-[#f4f7f6] transition-colors"
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : filteredManifestos.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <FileText className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No manifestos yet</h3>
          <p className="text-gray-600 mb-4">
            {search || filter !== 'all'
              ? 'No manifestos match your search criteria'
              : 'Start by generating a manifesto from citizen reports or creating a custom one'}
          </p>
          <div className="space-x-3">
            <button
              onClick={() => setShowGenerateModal(true)}
              className="px-4 py-2 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022]"
            >
              Generate from Reports
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 border border-[#0b2045] text-[#0b2045] rounded-lg hover:bg-[#f4f7f6]"
            >
              Create Custom
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredManifestos.map((manifesto) => (
            <div key={manifesto.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow hover:border-[#3c8d2e]/30">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-[#0b2045] mb-2">{manifesto.title}</h3>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        manifesto.source === 'CITIZEN_REPORTS' 
                          ? 'bg-[#3c8d2e]/10 text-[#0b2045]' 
                          : 'bg-[#0b2045]/5 text-[#0b2045]'
                      }`}>
                        {manifesto.source === 'CITIZEN_REPORTS' ? 'From Reports' : 'Custom'}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        manifesto.status === 'PUBLISHED' 
                          ? 'bg-[#3c8d2e]/10 text-[#3c8d2e]' 
                          : 'bg-[#fefce8] text-[#ca8a04]'
                      }`}>
                        {manifesto.statusDisplay}
                      </span>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">v{manifesto.version}</span>
                </div>

                {manifesto.summary && (
                  <p className="text-gray-600 text-sm mb-4">{manifesto.summary}</p>
                )}

                {manifesto.totalCitizenReportsAnalyzed && (
                  <div className="flex items-center text-sm text-[#0b2045] mb-4">
                    <BarChart3 size={14} className="mr-1" />
                    Based on {manifesto.totalCitizenReportsAnalyzed} citizen reports
                  </div>
                )}

                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Created: {new Date(manifesto.createdAt).toLocaleDateString()}</span>
                  {manifesto.isPublic && (
                    <span className="flex items-center text-[#3c8d2e]">
                      <Eye size={14} className="mr-1" />
                      Public
                    </span>
                  )}
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between">
                  <div className="flex space-x-2">
                    <button onClick={() => handleView(manifesto)} className="p-2 text-gray-400 hover:text-[#3c8d2e] transition-colors" title="View"><Eye size={16} /></button>
                    <button onClick={() => toast.success('Edit feature coming soon!')} className="p-2 text-gray-400 hover:text-[#0b2045] transition-colors" title="Edit"><Edit size={16} /></button>
                    <button onClick={() => printManifesto(manifesto)} className="p-2 text-gray-400 hover:text-[#0b2045] transition-colors" title="Print"><Printer size={16} /></button>
                    {!manifesto.isPublic && (
                      <button onClick={() => handlePublish(manifesto.id)} className="p-2 text-gray-400 hover:text-[#3c8d2e] transition-colors" title="Publish"><Share2 size={16} /></button>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => handleDownloadManifesto(manifesto)} className="p-2 text-gray-400 hover:text-[#0b2045] transition-colors" title="Download"><Download size={16} /></button>
                    <button onClick={() => handleDelete(manifesto.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showGenerateModal && (
        <GenerateManifestoModal
          aspirantId={aspirantId}
          onClose={() => setShowGenerateModal(false)}
          onSuccess={() => {
            setShowGenerateModal(false);
          }}
        />
      )}

      {showCreateModal && (
        <CreateManifestoModal
          aspirantId={aspirantId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};

export default Manifesto;